import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_MAP, StatusCode } from "@/models/MaintenanceWorkOrderJob";

type Props = {
  filterModel: GridFilterModel;
  setFilterModel: (fm: GridFilterModel) => void;
};

export function MaintenanceFilters({ filterModel, setFilterModel }: Props) {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<string>(""); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState<string>(""); // "YYYY-MM-DD"
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  const statusOptions = useMemo(
    () =>
      (Object.keys(STATUS_MAP) as unknown as StatusCode[]).map(code => ({
        value: Number(code),
        label: t(`maintenance.status.${STATUS_MAP[code]}`),
      })),
    [t],
  );

  useEffect(() => {
    const others = filterModel.items.filter(
      it =>
        !(
          it.field === "statusCode" ||
          (it.field === "createdAt" &&
            (it.operator === "onOrAfter" || it.operator === "onOrBefore"))
        ),
    );

    const items = [...others];

    if (selectedStatuses.length > 0) {
      items.push({
        id: "status-anyOf",
        field: "statusCode",
        operator: "isAnyOf",
        value: selectedStatuses,
      } as any);
    }

    // Dates: convert native YYYY-MM-DD to ISO bounds
    if (startDate) {
      const from = new Date(startDate); // local midnight
      from.setHours(0, 0, 0, 0);
      items.push({
        id: "createdAt-from",
        field: "createdAt",
        operator: "onOrAfter",
        value: from.toISOString(),
      } as any);
    }

    if (endDate) {
      const to = new Date(endDate);
      to.setHours(23, 59, 59, 999);
      items.push({
        id: "createdAt-to",
        field: "createdAt",
        operator: "onOrBefore",
        value: to.toISOString(),
      } as any);
    }

    const next: GridFilterModel = { ...filterModel, items };
    if (
      next.items.length !== filterModel.items.length ||
      next.items.some((n, i) => {
        const p = filterModel.items[i];
        return (
          !p ||
          p.field !== n.field ||
          p.operator !== n.operator ||
          String(p.value) !== String(n.value)
        );
      })
    ) {
      setFilterModel(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, startDate, endDate]);

  return (
    <Stack direction="row" gap={2} alignItems="center" sx={{ width: "100%" }}>
      <Autocomplete
        multiple
        options={statusOptions}
        getOptionLabel={o => o.label}
        value={statusOptions.filter(o => selectedStatuses.includes(o.value))}
        onChange={(_e, val) => setSelectedStatuses(val.map(v => v.value))}
        renderInput={params => (
          <TextField {...params} label={t("maintenance.statusCode", "Status")} />
        )}
        sx={{ minWidth: 260, maxWidth: 400 }}
      />

      <TextField
        label={t("maintenance.createdAt", "Created at") + " (" + t("from") + ")"}
        type="date"
        value={startDate}
        onChange={e => setStartDate(e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 200 }}
      />
      <TextField
        label={t("maintenance.createdAt", "Created at") + " (" + t("to") + ")"}
        type="date"
        value={endDate}
        onChange={e => setEndDate(e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ minWidth: 200 }}
      />
    </Stack>
  );
}
